import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Html5Qrcode,
} from 'html5-qrcode';

import './BirthdayCheckInScanner.css';


const API_URL =
  process.env.REACT_APP_API_URL ||
  'https://lsdcnodejs-api.onrender.com';


const SCANNER_ID =
  'birthday-checkin-reader';


/* ==========================================
   EXTRACT REGISTRATION CODE FROM QR
========================================== */

const extractRegistrationCode = (
  value = ''
) => {
  const raw =
    String(value).trim();

  if (!raw) {
    return '';
  }


  /**
   * QR could contain only:
   * COS-XXXXXXXX
   */
  if (
    /^COS-[A-Z0-9-]+$/i.test(
      raw
    )
  ) {
    return raw.toUpperCase();
  }


  /**
   * Your current QR contains:
   *
   * https://celebratingcos.site/verify/COS-XXXX
   */
  try {
    const url =
      new URL(raw);

    const parts =
      url.pathname
        .split('/')
        .filter(Boolean);

    const verifyIndex =
      parts.findIndex(
        (part) =>
          part.toLowerCase() ===
          'verify'
      );

    if (
      verifyIndex !== -1 &&
      parts[verifyIndex + 1]
    ) {
      const code =
        decodeURIComponent(
          parts[
            verifyIndex + 1
          ]
        )
          .trim()
          .toUpperCase();

      if (
        /^COS-[A-Z0-9-]+$/i.test(
          code
        )
      ) {
        return code;
      }
    }
  } catch {
    // Not a full URL.
  }


  /**
   * Also support:
   *
   * /verify/COS-XXXX
   */
  const marker =
    '/verify/';

  const position =
    raw
      .toLowerCase()
      .indexOf(marker);

  if (position !== -1) {
    const code =
      raw
        .slice(
          position +
            marker.length
        )
        .split('?')[0]
        .split('#')[0]
        .replace(/\/+$/, '')
        .trim()
        .toUpperCase();

    if (
      /^COS-[A-Z0-9-]+$/i.test(
        code
      )
    ) {
      return code;
    }
  }


  return '';
};


/* ==========================================
   DISPLAY HELPERS
========================================== */

const getAccessLabel = (
  accessType
) => {
  return accessType ===
    'banquet'
    ? 'Reception / Banquet'
    : 'Thanksgiving Service';
};


const formatDate = (
  value
) => {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  return date.toLocaleString(
    undefined,
    {
      dateStyle:
        'medium',

      timeStyle:
        'short',
    }
  );
};


/* ==========================================
   PASSWORD ICON
========================================== */

function EyeIcon({
  visible,
}) {
  return visible ? (

    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M3 3L21 21"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />

      <path
        d="M10.5 10.5A2.2 2.2 0 0013.5 13.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <path
        d="M9.5 4.3A10 10 0 0112 4c5.5 0 9 5.2 9 5.2a15 15 0 01-3 3.6M6 6C4.2 7.3 3 9.2 3 9.2s3.5 5.2 9 5.2c1 0 2-.2 2.8-.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>

  ) : (

    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M3 12s3.6-5.2 9-5.2S21 12 21 12s-3.6 5.2-9 5.2S3 12 3 12z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <circle
        cx="12"
        cy="12"
        r="2.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>

  );
}


/* ==========================================
   COMPONENT
========================================== */

export default function BirthdayCheckInScanner() {

  const scannerRef =
    useRef(null);

  /**
   * Prevent html5-qrcode from firing the same
   * detected QR multiple times before the
   * camera has stopped.
   */
  const scanLockedRef =
    useRef(false);


  const [
    adminKey,
    setAdminKey,
  ] = useState(
    () =>
      sessionStorage.getItem(
        'birthday_checkin_admin_key'
      ) || ''
  );


  const [
    showAdminKey,
    setShowAdminKey,
  ] = useState(false);


  const [
    accessType,
    setAccessType,
  ] = useState(
    () =>
      sessionStorage.getItem(
        'birthday_checkin_access'
      ) ||
      'thanksgiving'
  );


  const [
    scannerActive,
    setScannerActive,
  ] = useState(false);


  const [
    startingScanner,
    setStartingScanner,
  ] = useState(false);


  const [
    checking,
    setChecking,
  ] = useState(false);


  const [
    cameraError,
    setCameraError,
  ] = useState('');


  const [
    result,
    setResult,
  ] = useState(null);


  const [
    manualCode,
    setManualCode,
  ] = useState('');


  /* ========================================
     STOP SCANNER
  ======================================== */

  const stopScanner =
    useCallback(
      async () => {

        const scanner =
          scannerRef.current;

        scannerRef.current =
          null;


        if (!scanner) {
          setScannerActive(
            false
          );

          return;
        }


        try {
          await scanner.stop();
        } catch {
          // Ignore if scanner was already stopped.
        }


        try {
          await scanner.clear();
        } catch {
          // Ignore cleanup errors.
        }


        setScannerActive(
          false
        );

      },
      []
    );


  /* ========================================
     CHECK PASS WITH BACKEND
  ======================================== */

  const checkInPass =
    useCallback(
      async (
        registrationCode
      ) => {

        const key =
          adminKey.trim();


        if (!key) {

          setResult({
            type:
              'auth',

            title:
              'ADMIN KEY REQUIRED',

            message:
              'Enter the admin access key before scanning.',
          });

          return;
        }


        setChecking(true);

        setCameraError('');


        try {

          const response =
            await fetch(
              `${API_URL}/api/birthday/admin/check-in`,
              {
                method:
                  'POST',

                headers: {
                  'Content-Type':
                    'application/json',

                  'x-admin-key':
                    key,
                },

                body:
                  JSON.stringify({
                    registrationCode,

                    accessType,
                  }),
              }
            );


          let payload = {};

          try {
            payload =
              await response.json();
          } catch {
            payload = {};
          }


          /**
           * FIRST SUCCESSFUL ENTRY
           */
          if (
            response.ok &&
            payload.success
          ) {

            sessionStorage.setItem(
              'birthday_checkin_admin_key',
              key
            );

            sessionStorage.setItem(
              'birthday_checkin_access',
              accessType
            );


            navigator.vibrate?.(
              [100, 60, 100]
            );


            setResult({
              type:
                'success',

              title:
                'ACCESS GRANTED',

              message:
                payload.message ||
                'Guest checked in successfully.',

              data:
                payload.data || {},
            });


            return;
          }


          /**
           * ALREADY USED
           */
          if (
            response.status ===
              409 ||
            payload.code ===
              'PASS_ALREADY_USED'
          ) {

            navigator.vibrate?.(
              [300, 100, 300]
            );


            setResult({
              type:
                'used',

              title:
                'PASS ALREADY USED',

              message:
                payload.message ||
                'This pass has already been used at this entrance.',

              data:
                payload.data || {},
            });


            return;
          }


          /**
           * STAFF TRYING TO ENTER BANQUET
           */
          if (
            response.status ===
              403 ||
            payload.code ===
              'NO_BANQUET_ACCESS'
          ) {

            navigator.vibrate?.(
              [300, 100, 300]
            );


            setResult({
              type:
                'denied',

              title:
                'ACCESS DENIED',

              message:
                payload.message ||
                'This pass does not have access to this entrance.',

              data:
                payload.data || {},
            });


            return;
          }


          /**
           * INVALID PASS
           */
          if (
            response.status ===
              404 ||
            payload.code ===
              'PASS_NOT_FOUND'
          ) {

            setResult({
              type:
                'invalid',

              title:
                'INVALID PASS',

              message:
                payload.message ||
                'This QR code does not belong to a valid registration.',

              data:
                payload.data || {},
            });


            return;
          }


          /**
           * ADMIN KEY INVALID
           */
          if (
            response.status ===
            401
          ) {

            sessionStorage.removeItem(
              'birthday_checkin_admin_key'
            );


            setResult({
              type:
                'auth',

              title:
                'ADMIN ACCESS DENIED',

              message:
                payload.message ||
                'The admin access key is invalid.',
            });


            return;
          }


          /**
           * GENERIC ERROR
           */
          setResult({
            type:
              'error',

            title:
              'CHECK-IN FAILED',

            message:
              payload.message ||
              'Unable to check in this guest.',

            data:
              payload.data || {},
          });

        } catch (error) {

          console.error(
            'Check-in error:',
            error
          );


          setResult({
            type:
              'error',

            title:
              'NETWORK ERROR',

            message:
              'Unable to reach the server. Check the internet connection and try again.',
          });

        } finally {

          setChecking(false);

        }

      },
      [
        adminKey,
        accessType,
      ]
    );


  /* ========================================
     SUCCESSFUL QR DETECTION
  ======================================== */

  const handleQrDetected =
    useCallback(
      async (
        decodedText
      ) => {

        /**
         * Very important.
         *
         * html5-qrcode may report the same
         * visible QR several times per second.
         */
        if (
          scanLockedRef.current
        ) {
          return;
        }


        scanLockedRef.current =
          true;


        /**
         * Stop camera BEFORE contacting
         * the check-in endpoint.
         */
        await stopScanner();


        const registrationCode =
          extractRegistrationCode(
            decodedText
          );


        if (!registrationCode) {

          setResult({
            type:
              'invalid',

            title:
              'INVALID QR CODE',

            message:
              'This QR code is not a COS birthday registration pass.',
          });


          return;
        }


        await checkInPass(
          registrationCode
        );

      },
      [
        checkInPass,
        stopScanner,
      ]
    );


  /* ========================================
     START CAMERA
  ======================================== */

  const startScanner =
    useCallback(
      async () => {

        if (
          !adminKey.trim()
        ) {

          setCameraError(
            'Enter your admin access key first.'
          );

          return;
        }


        /**
         * Browser camera APIs require HTTPS.
         */
        if (
          !window.isSecureContext &&
          window.location.hostname !==
            'localhost'
        ) {

          setCameraError(
            'Camera scanning requires HTTPS. Open this page using https://'
          );

          return;
        }


        setResult(null);

        setCameraError('');

        setStartingScanner(
          true
        );

        scanLockedRef.current =
          false;


        await stopScanner();


        try {

          const scanner =
            new Html5Qrcode(
              SCANNER_ID
            );


          scannerRef.current =
            scanner;


          await scanner.start(

            /**
             * Prefer rear phone camera.
             */
            {
              facingMode:
                'environment',
            },


            /**
             * Scanner settings.
             */
            {
              fps:
                10,

              qrbox: {
                width:
                  240,

                height:
                  240,
              },
            },


            /**
             * QR FOUND
             */
            (
              decodedText
            ) => {

              handleQrDetected(
                decodedText
              );

            },


            /**
             * QR NOT FOUND IN CURRENT FRAME
             *
             * Do nothing.
             */
            () => {}

          );


          setScannerActive(
            true
          );

        } catch (error) {

          console.error(
            'Scanner camera error:',
            error
          );


          scannerRef.current =
            null;


          const message =
            String(
              error?.message ||
                error ||
                ''
            ).toLowerCase();


          if (
            message.includes(
              'permission'
            ) ||
            message.includes(
              'denied'
            ) ||
            message.includes(
              'notallowed'
            )
          ) {

            setCameraError(
              'Camera permission was denied. Allow camera access for this site and try again.'
            );

          } else {

            setCameraError(
              'Unable to start the camera. Make sure no other app is using it, then try again.'
            );

          }

        } finally {

          setStartingScanner(
            false
          );

        }

      },
      [
        adminKey,
        handleQrDetected,
        stopScanner,
      ]
    );


  /* ========================================
     MANUAL CHECK-IN
  ======================================== */

  const handleManualSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      if (checking) {
        return;
      }


      const registrationCode =
        extractRegistrationCode(
          manualCode
        );


      if (!registrationCode) {

        setResult({
          type:
            'invalid',

          title:
            'INVALID CODE',

          message:
            'Enter a valid COS registration code or verification URL.',
        });


        return;
      }


      await stopScanner();


      scanLockedRef.current =
        true;


      await checkInPass(
        registrationCode
      );

    };


  /* ========================================
     NEXT PERSON
  ======================================== */

  const handleNextPass =
    async () => {

      setResult(null);

      setManualCode('');

      scanLockedRef.current =
        false;


      await startScanner();

    };


  /* ========================================
     COMPONENT CLEANUP
  ======================================== */

  useEffect(() => {

    return () => {

      const scanner =
        scannerRef.current;


      scannerRef.current =
        null;


      if (scanner) {

        scanner
          .stop()
          .catch(() => {});

      }

    };

  }, []);


  /* ========================================
     RESULT DATA
  ======================================== */

  const data =
    result?.data || {};


  const checkInTime =
    formatDate(
      data.checkedInAt
    );


  /* ========================================
     PAGE
  ======================================== */

  return (

    <div className="checkin-page">

      <main className="checkin-container">


        <header className="checkin-header">

          <span>
            COS Birthday Celebration
          </span>

          <h1>
            Event Check-In
          </h1>

          <p>
            Scan each guest's QR pass to validate
            and record entry.
          </p>

        </header>


        {/* ==================================
            SETTINGS
        ================================== */}

        <section className="checkin-settings">


          <div className="checkin-field">

            <label htmlFor="scanner-admin-key">
              Admin Access Key
            </label>


            <div className="checkin-password">

              <input

                id="scanner-admin-key"

                type={
                  showAdminKey
                    ? 'text'
                    : 'password'
                }

                value={
                  adminKey
                }

                onChange={(
                  event
                ) => {

                  setAdminKey(
                    event.target.value
                  );

                  setCameraError('');

                }}

                placeholder="Enter admin key"

                disabled={
                  scannerActive ||
                  checking
                }

              />


              <button

                type="button"

                onClick={() =>
                  setShowAdminKey(
                    (current) =>
                      !current
                  )
                }

                aria-label={
                  showAdminKey
                    ? 'Hide password'
                    : 'Show password'
                }

              >

                <EyeIcon
                  visible={
                    showAdminKey
                  }
                />

              </button>

            </div>

          </div>


          <div className="checkin-field">

            <label htmlFor="scanner-access-type">
              Check-In Point
            </label>


            <select

              id="scanner-access-type"

              value={
                accessType
              }

              onChange={(
                event
              ) => {

                setAccessType(
                  event.target.value
                );

                setResult(null);

              }}

              disabled={
                scannerActive ||
                checking
              }

            >

              <option value="thanksgiving">
                Thanksgiving Service
              </option>

              <option value="banquet">
                Reception / Banquet
              </option>

            </select>

          </div>

        </section>


        <div className="checkin-gate">

          <span>
            ACTIVE CHECK-IN POINT
          </span>

          <strong>
            {getAccessLabel(
              accessType
            )}
          </strong>

        </div>


        {/* ==================================
            CAMERA
        ================================== */}

        <section className="checkin-camera-card">


          <div
            id={SCANNER_ID}
            className="checkin-reader"
          />


          {!scannerActive &&
            !startingScanner &&
            !checking &&
            !result && (

              <div className="checkin-camera-empty">

                <div className="checkin-camera-symbol">
                  ▣
                </div>

                <strong>
                  Ready to Scan
                </strong>

                <p>
                  Point the rear camera at the
                  guest's QR code.
                </p>


                <button
                  type="button"
                  className="checkin-start-button"
                  onClick={
                    startScanner
                  }
                >
                  Start QR Scanner
                </button>

              </div>

            )}


          {startingScanner && (

            <div className="checkin-loading">
              Starting camera...
            </div>

          )}


          {checking && (

            <div className="checkin-loading">
              Checking pass...
            </div>

          )}


          {cameraError && (

            <div
              className="checkin-error"
              role="alert"
            >
              {cameraError}
            </div>

          )}


          {scannerActive && (

            <div className="checkin-live">

              <span />

              Camera Live

              <button
                type="button"
                onClick={
                  stopScanner
                }
              >
                Stop Camera
              </button>

            </div>

          )}

        </section>


        {/* ==================================
            CHECK-IN RESULT
        ================================== */}

        {result && (

          <section
            className={`checkin-result checkin-result--${result.type}`}
          >


            <div className="checkin-result-icon">

              {result.type ===
              'success'
                ? '✓'
                : '×'}

            </div>


            <span className="checkin-result-title">
              {result.title}
            </span>


            {data.fullName && (

              <h2>

                {data.title &&
                  `${data.title} `}

                {data.fullName}

              </h2>

            )}


            <p>
              {result.message}
            </p>


            {(data.registrationCode ||
              data.type ||
              data.accessType ||
              checkInTime) && (

              <div className="checkin-result-info">


                {data.registrationCode && (

                  <div>

                    <span>
                      Registration Code
                    </span>

                    <strong>
                      {
                        data.registrationCode
                      }
                    </strong>

                  </div>

                )}


                {data.type && (

                  <div>

                    <span>
                      Category
                    </span>

                    <strong>

                      {data.type ===
                      'dignitary'
                        ? 'VIP / Dignitary'
                        : 'Staff'}

                    </strong>

                  </div>

                )}


                <div>

                  <span>
                    Check-In Point
                  </span>

                  <strong>
                    {getAccessLabel(
                      data.accessType ||
                        accessType
                    )}
                  </strong>

                </div>


                {checkInTime && (

                  <div>

                    <span>

                      {result.type ===
                      'used'
                        ? 'Previously Scanned'
                        : 'Checked In'}

                    </span>

                    <strong>
                      {checkInTime}
                    </strong>

                  </div>

                )}

              </div>

            )}


            <button
              type="button"
              className="checkin-next-button"
              onClick={
                handleNextPass
              }
            >
              Scan Next Pass
            </button>

          </section>

        )}


        {/* ==================================
            MANUAL FALLBACK
        ================================== */}

        {/* <section className="checkin-manual">

          <div>

            <span>
              CAMERA FALLBACK
            </span>

            <strong>
              Manual Check-In
            </strong>

          </div>


          <form
            onSubmit={
              handleManualSubmit
            }
          >

            <input

              type="text"

              value={
                manualCode
              }

              onChange={(
                event
              ) =>
                setManualCode(
                  event.target.value
                )
              }

              placeholder="COS-XXXXXXXX or verification URL"

              disabled={
                checking
              }

            />


            <button
              type="submit"
              disabled={
                checking ||
                !manualCode.trim()
              }
            >
              Check Pass
            </button>

          </form>

        </section> */}


        <p className="checkin-note">

          A guest opening their normal verification
          page does not consume their pass. Entry is
          recorded only through this authenticated
          admin scanner.

        </p>


      </main>

    </div>

  );

}